import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useWarehouseLocations from "../../data/useWarehouseLocations";
import useSites from "../../data/useSites";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { IWarehouseLocation } from "../../models/Location";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import printHtml from "../../utils/printHtml";

type FormValues = {
  displayName: string;
  capacity: number;
  isExcluded: boolean;
  siteId?: string | null;
};

interface LocationsModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  location?: IWarehouseLocation;
}

export default function WarehouseLocationsModal({ open, setOpen, location }: LocationsModalProps) {
  const { t } = useTranslation("stock");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Warehouse Location"));
  const { deleteHandlers } = useDefaultCRUDHandlers(t("Warehouse Location"));
  const { generateWarehouseLabel } = useLabelGenerator();
  const { update, create, remove } = useWarehouseLocations();
  const { sites: allSites } = useSites();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();

  // Only offered once a site exists: single-depot yards never see the field.
  const siteOptions = [
    { id: "", name: t("None") },
    ...(allSites.data?.data ?? []).map((site) => ({
      id: site.id,
      name: site.name,
    }))
  ];
  const hasSites = (allSites.data?.data?.length ?? 0) > 0;

  const onSubmit = (data: any) => {

    if (data.siteId === "" || data.siteId === undefined) {
      data.siteId = null;
    }

    const updatedLocation = {
      ...location,
      ...data
    };

    if (location !== undefined) {
      update.mutate(updatedLocation, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      create.mutate(updatedLocation, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  }

  const handleDelete = () => {
    remove.mutate(location?.id!, {
      onSuccess: (data) => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  const handlePrint = async () => {
    const html = await generateWarehouseLabel(location!);
    printHtml(html);
  };

  useEffect(() => {

    if (location !== undefined) {
      reset({
        displayName: location.displayName,
        capacity: location.capacity,
        isExcluded: location.isExcluded,
        siteId: location.siteId ?? ""
      });
    } else {
      reset({ siteId: "" })
    }
  }, [location, open, reset]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={t("Delete Location")} message={t("Are you sure you want to delete this location?\n This action cannot be undone.")}
        confirmButtonText={t("Delete")} onConfirm={handleDelete} isLoading={false}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <div className="flex justify-between">
          <h1 className="text-xl">{location ? t("Edit a Location") : t("Add a Location")}</h1>
          {location &&
            <ButtonInput label={t("Print Label")} isSubmit={false} onClick={() => handlePrint()} classes={undefined} />
          }
        </div>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label={t("Display Name")} registerName="displayName" register={register} />

              <NumberInput label={t("Capacity")} registerName="capacity" register={register} />

              <CheckboxInput label={`${t("Excluded")} `} registerName="isExcluded" register={register} />

              {hasSites && (
                <SelectInput label={t("Site")} registerName="siteId" register={register} watch={watch} setValue={setValue} options={siteOptions} />
              )}

              <ButtonInput label={location ? t("Save Changes") : t("Create")} isSubmit={true} onClick={undefined} classes={undefined} />
              {location !== undefined && (
                <ButtonInput label={t("Delete")} isSubmit={false} onClick={() => (setDeleteOpen(true))} classes={"!bg-red-500 hover:!bg-red-600"} />
              )}
            </div>
          </form>
        </div>
      </Modal>

    </>
  );
}
